import { useId, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@material-ui/core';
import { Entity } from '@backstage/catalog-model';
import {
  Content,
  Header,
  Link,
  Page,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsyncRetry } from 'react-use';

const MANAGED_BY_LOCATION = 'backstage.io/managed-by-location';
const ALL_TEAMS = '__all__';

export type MigrationSource = 'JSON' | 'YAML' | 'Unknown';

type MigrationRow = {
  name: string;
  title: string;
  namespace: string;
  team: string;
  source: MigrationSource;
  sourceLocation?: string;
  repositoryUrl?: string;
  diagnostic?: string;
};

type TeamMigration = {
  team: string;
  migrated: number;
  json: number;
  total: number;
  percentage: number;
};

type SourceFilter = MigrationSource | 'All';

const useThermometerStyles = makeStyles(theme => ({
  thermometer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  bulb: {
    width: 26,
    height: 26,
    flex: '0 0 26px',
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${theme.palette.secondary.light}, ${theme.palette.primary.main})`,
    border: `2px solid ${theme.palette.primary.dark}`,
    boxShadow: `inset 0 0 0 4px ${theme.palette.background.paper}`,
    zIndex: 1,
  },
  tube: {
    height: 16,
    flex: 1,
    marginLeft: -4,
    padding: 2,
    overflow: 'hidden',
    border: `2px solid ${theme.palette.primary.dark}`,
    borderLeft: 0,
    borderRadius: '0 12px 12px 0',
    backgroundColor: theme.palette.grey[200],
  },
  fill: {
    height: '100%',
    minWidth: 0,
    borderRadius: '0 8px 8px 0',
    transformOrigin: 'left center',
    background: `linear-gradient(90deg, ${theme.palette.secondary.light}, ${theme.palette.primary.main})`,
    animation: '$fillThermometer 700ms ease-out',
    transition: 'width 500ms ease-in-out',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      transition: 'none',
    },
  },
  '@keyframes fillThermometer': {
    from: { transform: 'scaleX(0)' },
    to: { transform: 'scaleX(1)' },
  },
  teamButton: {
    width: '100%',
    padding: theme.spacing(1),
    border: 0,
    borderRadius: theme.shape.borderRadius,
    background: 'transparent',
    color: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    '&:hover, &:focus-visible': {
      backgroundColor: theme.palette.action.hover,
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  scatterPlot: {
    width: '100%',
    height: 'auto',
    minHeight: 280,
    overflow: 'visible',
  },
  scatterPoint: {
    cursor: 'pointer',
    stroke: theme.palette.background.paper,
    strokeWidth: 2,
    transition: 'r 200ms ease, opacity 200ms ease',
    '&:hover, &:focus': {
      opacity: 1,
      stroke: theme.palette.primary.dark,
      strokeWidth: 4,
      outline: 'none',
    },
  },
}));

function MigrationThermometer({
  value,
  max,
  label,
}: {
  value: number;
  max: number;
  label: string;
}) {
  const classes = useThermometerStyles();
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <div
      className={classes.thermometer}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
    >
      <div className={classes.bulb} aria-hidden="true" />
      <div className={classes.tube} aria-hidden="true">
        <div className={classes.fill} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

export function getMigrationSource(entity: Entity): MigrationSource {
  const location =
    entity.metadata.annotations?.[MANAGED_BY_LOCATION]?.toLowerCase() ?? '';

  if (/\.json(?:$|[?#])/.test(location)) {
    return 'JSON';
  }
  if (/\.ya?ml(?:$|[?#])/.test(location)) {
    return 'YAML';
  }
  return 'Unknown';
}

export function getOwningTeam(entity: Entity): string {
  const owner = entity.spec?.owner;
  if (typeof owner !== 'string' || owner.trim() === '') {
    return 'Unowned';
  }

  return owner.split('/').pop() ?? owner;
}

export function getMigrationDiagnostic(entity: Entity): string | undefined {
  const location = entity.metadata.annotations?.[MANAGED_BY_LOCATION];
  if (!location) {
    return 'Missing managed-by-location annotation';
  }
  if (getMigrationSource(entity) !== 'Unknown') {
    return undefined;
  }
  if (!/^(url|file):/i.test(location)) {
    return 'Unsupported managed location type';
  }
  if (/^url:/i.test(location)) {
    try {
      const parsedLocation = new URL(location.replace(/^url:/i, ''));
      if (!['http:', 'https:'].includes(parsedLocation.protocol)) {
        return 'Unsupported managed location URL protocol';
      }
    } catch {
      return 'Malformed managed location URL';
    }
  }
  return 'Managed location is not a JSON or YAML file';
}

function getRepositoryUrl(entity: Entity): string | undefined {
  const annotations = entity.metadata.annotations ?? {};
  const projectSlug = annotations['github.com/project-slug'];
  if (projectSlug && projectSlug !== 'none') {
    return `https://github.com/${projectSlug}`;
  }
  const sourceLocation = annotations['backstage.io/source-location']?.replace(
    /^url:/,
    '',
  );
  if (sourceLocation) {
    return sourceLocation;
  }
  const managedLocation = annotations[MANAGED_BY_LOCATION];
  const githubRepository = managedLocation?.match(
    /github\.com\/([^/]+\/[^/]+)/i,
  )?.[1];
  return githubRepository
    ? `https://github.com/${githubRepository}`
    : undefined;
}

function SummaryCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: number | string;
  detail: string;
}) {
  return (
    <Paper variant="outlined">
      <Box p={2}>
        <Typography variant="overline" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="h4">{value}</Typography>
        <Typography variant="body2" color="textSecondary">
          {detail}
        </Typography>
      </Box>
    </Paper>
  );
}

function TeamMigrationScatter({
  teams,
  selectedTeam,
  onSelectTeam,
}: {
  teams: TeamMigration[];
  selectedTeam: string;
  onSelectTeam: (team: string) => void;
}) {
  const classes = useThermometerStyles();
  const chartTitleId = useId();
  const chartDescriptionId = useId();
  const width = 760;
  const height = 310;
  const left = 62;
  const right = 24;
  const top = 20;
  const bottom = 52;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const maxTotal = Math.max(1, ...teams.map(team => team.total));
  const maxJson = Math.max(1, ...teams.map(team => team.json));
  const percentageTicks = [0, 25, 50, 75, 100];

  return (
    <Paper variant="outlined">
      <Box p={3}>
        <Typography variant="h5" gutterBottom>
          Progress versus team size
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Bubble size represents JSON components remaining. Select a team to
          filter the component table.
        </Typography>
        {teams.length === 0 ? (
          <Box mt={2}>
            <Typography>No teams match the minimum size.</Typography>
          </Box>
        ) : (
          <svg
            className={classes.scatterPlot}
            viewBox={`0 0 ${width} ${height}`}
            role="group"
            aria-labelledby={`${chartTitleId} ${chartDescriptionId}`}
          >
            <title id={chartTitleId}>
              Team migration progress compared with team size
            </title>
            <desc id={chartDescriptionId}>
              Interactive scatter plot. Select a team point to filter the
              component table.
            </desc>
            {percentageTicks.map(tick => {
              const x = left + (tick / 100) * plotWidth;
              return (
                <g key={tick}>
                  <line
                    x1={x}
                    x2={x}
                    y1={top}
                    y2={top + plotHeight}
                    stroke="currentColor"
                    opacity={0.12}
                  />
                  <text
                    x={x}
                    y={height - 25}
                    textAnchor="middle"
                    fontSize="12"
                    fill="currentColor"
                  >
                    {tick}%
                  </text>
                </g>
              );
            })}
            <line
              x1={left}
              x2={left}
              y1={top}
              y2={top + plotHeight}
              stroke="currentColor"
              opacity={0.5}
            />
            <line
              x1={left}
              x2={left + plotWidth}
              y1={top + plotHeight}
              y2={top + plotHeight}
              stroke="currentColor"
              opacity={0.5}
            />
            <text
              x={left + plotWidth / 2}
              y={height - 4}
              textAnchor="middle"
              fontSize="13"
              fill="currentColor"
            >
              Migration percentage
            </text>
            <text
              x={16}
              y={top + plotHeight / 2}
              textAnchor="middle"
              fontSize="13"
              fill="currentColor"
              transform={`rotate(-90 16 ${top + plotHeight / 2})`}
            >
              Total components
            </text>
            <text
              x={left - 8}
              y={top + 5}
              textAnchor="end"
              fontSize="12"
              fill="currentColor"
            >
              {maxTotal}
            </text>
            <text
              x={left - 8}
              y={top + plotHeight}
              textAnchor="end"
              fontSize="12"
              fill="currentColor"
            >
              0
            </text>
            {teams.map(team => {
              const x = left + (team.percentage / 100) * plotWidth;
              const y = top + plotHeight - (team.total / maxTotal) * plotHeight;
              const radius = 6 + Math.sqrt(team.json / maxJson) * 12;
              const label = `${team.team}: ${team.percentage}% migrated, ${team.total} total components, ${team.json} JSON remaining`;
              return (
                <circle
                  key={team.team}
                  className={classes.scatterPoint}
                  cx={x}
                  cy={y}
                  r={radius}
                  fill={
                    selectedTeam === team.team
                      ? 'currentColor'
                      : 'var(--migration-point-color, #79589f)'
                  }
                  opacity={selectedTeam === team.team ? 1 : 0.68}
                  role="button"
                  tabIndex={0}
                  aria-label={label}
                  onClick={() => onSelectTeam(team.team)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelectTeam(team.team);
                    }
                  }}
                >
                  <title>{label}</title>
                </circle>
              );
            })}
          </svg>
        )}
      </Box>
    </Paper>
  );
}

const columns: TableColumn<MigrationRow>[] = [
  {
    title: 'Component',
    field: 'title',
    render: row => (
      <Link
        to={`/catalog/${encodeURIComponent(
          row.namespace,
        )}/component/${encodeURIComponent(row.name)}`}
      >
        {row.title}
      </Link>
    ),
  },
  { title: 'Team', field: 'team' },
  {
    title: 'Catalog source',
    field: 'source',
    render: row => (
      <Chip
        size="small"
        label={row.source}
        color={row.source === 'YAML' ? 'primary' : 'default'}
        variant={row.source === 'Unknown' ? 'outlined' : 'default'}
      />
    ),
  },
  {
    title: 'Source file',
    field: 'sourceLocation',
    render: row =>
      row.sourceLocation ? (
        <Link to={row.sourceLocation.replace(/^url:/, '')}>View source</Link>
      ) : (
        'Unavailable'
      ),
  },
  {
    title: 'Diagnostic',
    field: 'diagnostic',
    render: row => row.diagnostic ?? '—',
  },
  {
    title: 'Actions',
    sorting: false,
    render: row => (
      <Box display="flex" flexWrap="wrap">
        {row.repositoryUrl && (
          <Box mr={1}>
            <Link to={row.repositoryUrl}>Repository</Link>
          </Box>
        )}
        {row.source === 'JSON' && (
          <>
            <Box mr={1}>
              <Link to="/docs/default/component/backstage/component-onboarding#migrating-to-yaml">
                Migration guide
              </Link>
            </Box>
            <Link to="/entity-validation">Validate YAML</Link>
          </>
        )}
      </Box>
    ),
  },
];

export function MigrationPage() {
  const classes = useThermometerStyles();
  const catalogApi = useApi(catalogApiRef);
  const [selectedTeam, setSelectedTeam] = useState(ALL_TEAMS);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('All');
  const [minimumTeamSize, setMinimumTeamSize] = useState(0);
  const entities = useAsyncRetry(async () => {
    const response = await catalogApi.getEntities({
      filter: { kind: 'Component' },
    });
    return { items: response.items, loadedAt: new Date() };
  }, [catalogApi]);

  const rows = useMemo<MigrationRow[]>(
    () =>
      (entities.value?.items ?? [])
        .map(entity => ({
          name: entity.metadata.name,
          title: entity.metadata.title ?? entity.metadata.name,
          namespace: entity.metadata.namespace ?? 'default',
          team: getOwningTeam(entity),
          source: getMigrationSource(entity),
          sourceLocation: entity.metadata.annotations?.[MANAGED_BY_LOCATION],
          repositoryUrl: getRepositoryUrl(entity),
          diagnostic: getMigrationDiagnostic(entity),
        }))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [entities.value?.items],
  );

  const teams = useMemo(
    () => [...new Set(rows.map(row => row.team))].sort(),
    [rows],
  );
  const teamFilteredRows = useMemo(
    () =>
      selectedTeam === ALL_TEAMS
        ? rows
        : rows.filter(row => row.team === selectedTeam),
    [rows, selectedTeam],
  );
  const filteredRows = useMemo(
    () =>
      sourceFilter === 'All'
        ? teamFilteredRows
        : teamFilteredRows.filter(row => row.source === sourceFilter),
    [sourceFilter, teamFilteredRows],
  );
  const yamlCount = teamFilteredRows.filter(
    row => row.source === 'YAML',
  ).length;
  const jsonCount = teamFilteredRows.filter(
    row => row.source === 'JSON',
  ).length;
  const unknownCount = teamFilteredRows.length - yamlCount - jsonCount;
  const classifiedCount = yamlCount + jsonCount;
  const migratedPercent = classifiedCount
    ? Math.round((yamlCount / classifiedCount) * 100)
    : 0;
  const allTeamMigrations = useMemo<TeamMigration[]>(() => {
    const byTeam = new Map<
      string,
      { migrated: number; json: number; total: number }
    >();

    for (const row of rows) {
      if (row.team === 'Unowned') {
        continue;
      }
      const counts = byTeam.get(row.team) ?? {
        migrated: 0,
        json: 0,
        total: 0,
      };
      counts.total += 1;
      if (row.source === 'YAML') {
        counts.migrated += 1;
      } else if (row.source === 'JSON') {
        counts.json += 1;
      }
      byTeam.set(row.team, counts);
    }

    return [...byTeam.entries()]
      .map(([team, counts]) => ({
        team,
        ...counts,
        percentage: Math.round((counts.migrated / counts.total) * 100),
      }))
      .sort(
        (a, b) =>
          b.percentage - a.percentage ||
          b.migrated - a.migrated ||
          a.team.localeCompare(b.team),
      );
  }, [rows]);
  const comparableTeams = useMemo(
    () => allTeamMigrations.filter(team => team.total >= minimumTeamSize),
    [allTeamMigrations, minimumTeamSize],
  );
  const teamMigrations = comparableTeams.slice(0, 10);

  return (
    <Page themeId="tool">
      <Header
        title="Catalog Migration"
        subtitle="Track components moving from legacy JSON inventory files to catalog-info.yaml"
      />
      <Content>
        {entities.loading && !entities.value && <Progress />}
        {entities.error && !entities.value && (
          <>
            <ResponseErrorPanel error={entities.error} />
            <Box mt={2}>
              <Button variant="outlined" onClick={entities.retry}>
                Retry
              </Button>
            </Box>
          </>
        )}
        {entities.error && entities.value && (
          <Box mb={2}>
            <ResponseErrorPanel error={entities.error} />
          </Box>
        )}
        {entities.value && (
          <>
            <Box mb={3}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel id="migration-team-label">Team</InputLabel>
                    <Select
                      labelId="migration-team-label"
                      id="migration-team"
                      value={selectedTeam}
                      onChange={event =>
                        setSelectedTeam(event.target.value as string)
                      }
                      label="Team"
                    >
                      <MenuItem value={ALL_TEAMS}>All teams</MenuItem>
                      {teams.map(team => (
                        <MenuItem key={team} value={team}>
                          {team}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel id="minimum-team-size-label">
                      Minimum team size
                    </InputLabel>
                    <Select
                      labelId="minimum-team-size-label"
                      id="minimum-team-size"
                      value={minimumTeamSize}
                      onChange={event =>
                        setMinimumTeamSize(Number(event.target.value))
                      }
                      label="Minimum team size"
                    >
                      <MenuItem value={0}>Any size</MenuItem>
                      <MenuItem value={5}>5+ components</MenuItem>
                      <MenuItem value={10}>10+ components</MenuItem>
                      <MenuItem value={25}>25+ components</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={6}>
                  <Box display="flex" alignItems="center">
                    <Button
                      variant="outlined"
                      onClick={entities.retry}
                      disabled={entities.loading}
                    >
                      {entities.loading ? 'Refreshing…' : 'Refresh'}
                    </Button>
                    <Box ml={2}>
                      <Typography variant="caption" color="textSecondary">
                        Last refreshed{' '}
                        {entities.value.loadedAt.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  label="Migration progress"
                  value={`${migratedPercent}%`}
                  detail={`${yamlCount} of ${classifiedCount} classified components`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  label="YAML"
                  value={yamlCount}
                  detail="Using catalog-info.yaml"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  label="JSON"
                  value={jsonCount}
                  detail="Still needs migration"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  label="Unknown"
                  value={unknownCount}
                  detail="Source could not be classified"
                />
              </Grid>
            </Grid>

            <Box mt={3} mb={3}>
              <Paper variant="outlined">
                <Box p={3}>
                  <Typography variant="h5" gutterBottom>
                    Overall migration thermometer
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {migratedPercent}% of classified components use YAML
                  </Typography>
                  <MigrationThermometer
                    value={migratedPercent}
                    max={100}
                    label="YAML migration progress"
                  />
                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                    <Typography variant="caption" color="textSecondary">
                      0%
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      100%
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>

            <Paper variant="outlined">
              <Box p={3}>
                <Typography variant="h5" gutterBottom>
                  Team migration comparison
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ranked by the percentage of each team’s total components using
                  catalog-info.yaml.
                </Typography>
                <Box mt={2}>
                  {teamMigrations.length === 0 ? (
                    <Typography>No teams match the minimum size.</Typography>
                  ) : (
                    teamMigrations.map(team => (
                      <Box key={team.team} mb={2}>
                        <button
                          type="button"
                          className={classes.teamButton}
                          onClick={() => setSelectedTeam(team.team)}
                          aria-label={`Filter components by ${team.team}`}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Typography variant="subtitle2">
                                  {team.team}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={`${team.total} total`}
                                  variant="outlined"
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <MigrationThermometer
                                value={team.percentage}
                                max={100}
                                label={`${team.team} migrated components`}
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Typography variant="body2">
                                {team.migrated} migrated · {team.percentage}%
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {team.migrated} of {team.total} total components
                              </Typography>
                            </Grid>
                          </Grid>
                        </button>
                      </Box>
                    ))
                  )}
                </Box>
              </Box>
            </Paper>

            <Box mt={3}>
              <TeamMigrationScatter
                teams={comparableTeams}
                selectedTeam={selectedTeam}
                onSelectTeam={setSelectedTeam}
              />
            </Box>

            <Box mt={3}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel id="migration-source-label">Source</InputLabel>
                    <Select
                      labelId="migration-source-label"
                      id="migration-source"
                      value={sourceFilter}
                      onChange={event =>
                        setSourceFilter(event.target.value as SourceFilter)
                      }
                      label="Source"
                    >
                      {(['All', 'JSON', 'YAML', 'Unknown'] as const).map(
                        source => (
                          <MenuItem key={source} value={source}>
                            {source}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <Typography variant="body2">
                    Showing {filteredRows.length} of {teamFilteredRows.length}{' '}
                    components
                    {sourceFilter === 'All'
                      ? ''
                      : ` with ${sourceFilter} source`}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Source filters this work queue; team metrics above continue
                    to include every component for a fair comparison.
                  </Typography>
                </Grid>
              </Grid>
              <Box mt={2}>
                <Table
                  title={`Components (${filteredRows.length}) — ${sourceFilter}`}
                  options={{ paging: true, pageSize: 20, search: true }}
                  columns={columns}
                  data={filteredRows}
                  emptyContent={
                    <Typography>
                      No {sourceFilter === 'All' ? '' : `${sourceFilter} `}
                      components found for this team.
                    </Typography>
                  }
                />
              </Box>
            </Box>
          </>
        )}
      </Content>
    </Page>
  );
}
